/**
 * sn-kaufboxen — Paketwahl (1 / 3 / 5 Dosen) fuer Horizon. Markup: blocks/sn-kaufboxen.liquid
 *
 * Ein Klick auf eine Zeile waehlt die Variante ueber Horizons eigenen Varianten-Waehler
 * (versteckt im selben Produktbereich). Horizon aktualisiert dann Preis, Kaufleiste, URL und
 * das Kaufformular selbst. Zusaetzlich setzen wir input[name=id] direkt und pruefen es noch
 * einmal unmittelbar vor dem Absenden (Lehre aus dem Dawn-Fehler vom 19.–21.09.2026).
 */
class SnKaufboxen extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    this.rows = Array.from(this.querySelectorAll('.sn-kb__zeile'));
    this.scope = this.closest('.product-details') || this.closest('.shopify-section') || document;

    this.rows.forEach((row) => row.addEventListener('click', () => {
      const h = this.querySelector('.sn-kb__hinweis');
      if (h) h.hidden = true;
      this.choose(row);
    }));
    this.addEventListener('keydown', this.onKey);

    // Einzeldose auf 3 (bzw. 5) hochgezaehlt → ins passende Sparpaket wechseln, sonst fehlt der Paketrabatt
    // (Ken 28.09.2026: „1 Dose + Menge 3" war teurer als „3 Dosen").
    this.onQuantity = (event) => {
      if (event.detail?.cartLine || !(event.target instanceof Node) || !this.scope.contains(event.target)) return;
      const aktiv = this.active();
      if (!aktiv || Number(aktiv.dataset.qty || 1) !== 1) return;
      const menge = Number(event.detail?.quantity);
      const paket = this.rows.find((r) => !r.disabled && Number(r.dataset.qty) === menge && menge > 1);
      if (!paket) return;
      this.choose(paket);
      this.zeigeHinweis(menge);
    };
    document.addEventListener('quantity-selector:update', this.onQuantity);

    // Sicherheitsnetz in der Erfassungsphase, also vor Horizons eigenem Submit-Handler.
    this.onSubmitCapture = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !this.scope.contains(form)) return;
      if (!form.querySelector('input[name="id"]')) return;
      this.writeId(this.active(), form);
    };
    document.addEventListener('submit', this.onSubmitCapture, true);

    // Die Kaufleiste unten sendet nicht selbst ab, sondern klickt den echten Knopf —
    // trotzdem hier absichern, falls Horizon das spaeter aendert.
    this.onClickCapture = (event) => {
      const t = event.target instanceof Element ? event.target : null;
      if (t && t.closest('.sticky-add-to-cart__button, .add-to-cart-button')) this.writeId(this.active());
    };
    document.addEventListener('click', this.onClickCapture, true);

    const start = this.active();
    this.syncQuantity(start, false);
    // Ohne ?variant= in der URL: 3 Dosen vorwaehlen. Das Formular bekommt die Variante sofort (wer blitzschnell
    // kauft, bekommt 3 Dosen). Horizons Waehler holt dafuer die ganze Seite neu (~100 KB) — das darf erst NACH
    // dem Laden passieren, sonst bremst es das erste Bild im langsamen Handynetz (gemessen 26.09.).
    if (start && this.hasAttribute('data-apply-presel')) {
      this.writeId(start);
      const spaeter = () => {
        const los = () => { if (start.classList.contains('is-active')) this.pickInHorizon(start); };
        if ('requestIdleCallback' in window) requestIdleCallback(los, { timeout: 2500 });
        else setTimeout(los, 800);
      };
      if (document.readyState === 'complete') spaeter();
      else window.addEventListener('load', spaeter, { once: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onSubmitCapture, true);
    document.removeEventListener('click', this.onClickCapture, true);
    document.removeEventListener('quantity-selector:update', this.onQuantity);
  }

  zeigeHinweis(menge) {
    const el = this.querySelector('.sn-kb__hinweis');
    if (!el) return;
    el.textContent = (el.dataset.text || '').replace('{n}', String(menge));
    el.hidden = false;
  }

  /** @returns {HTMLButtonElement | undefined} */
  active() {
    return this.rows.find((r) => r.classList.contains('is-active')) || this.rows[0];
  }

  /** @param {HTMLButtonElement} row */
  choose(row) {
    if (!row || row.disabled || row.classList.contains('is-active')) return;
    this.rows.forEach((r) => {
      const on = r === row;
      r.classList.toggle('is-active', on);
      r.setAttribute('aria-checked', String(on));
    });
    this.syncQuantity(row, true);
    this.pickInHorizon(row);
    this.writeId(row);
    document.dispatchEvent(new CustomEvent('sn:variant', { detail: { id: Number(row.dataset.variantId) } }));
  }

  /** Pfeiltasten wie bei einer echten Radiogruppe. */
  onKey = (event) => {
    if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(event.key)) return;
    const i = this.rows.indexOf(/** @type {any} */ (document.activeElement));
    if (i < 0) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
    const next = this.rows[(i + step + this.rows.length) % this.rows.length];
    next.focus();
    this.choose(next);
  };

  /** Setzt den passenden Radioknopf in Horizons Varianten-Waehler und loest dessen change aus. */
  pickInHorizon(row) {
    const picker = this.scope.querySelector('variant-picker');
    if (!picker) return false;
    const values = [row.dataset.option1, row.dataset.option2, row.dataset.option3].filter(Boolean);
    let changed = false;
    const fieldsets = picker.querySelectorAll('fieldset');
    values.forEach((val, idx) => {
      const fs = fieldsets[idx];
      if (fs) {
        const input = Array.from(fs.querySelectorAll('input[type="radio"]')).find((el) => el.value === val);
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          changed = true;
        }
        return;
      }
      // Dropdown-Darstellung des Waehlers
      const selects = picker.querySelectorAll('select');
      const sel = selects[idx];
      if (sel && sel.value !== val) {
        sel.value = val;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        changed = true;
      }
    });
    return changed;
  }

  /** Schreibt die Variante direkt ins Kaufformular (ohne Ereignis, damit nichts neu rendert). */
  writeId(row, form) {
    if (!row || !row.dataset.variantId) return;
    const forms = form ? [form] : this.scope.querySelectorAll('form[data-type="add-to-cart-form"], product-form-component form');
    forms.forEach((f) => {
      const input = f.querySelector('input[name="id"]');
      if (input && input.value !== row.dataset.variantId) input.value = row.dataset.variantId;
    });
  }

  /** Mengenwaehler nur bei der Einzeldose; bei Paketen zurueck auf 1. */
  syncQuantity(row, reset) {
    const single = !row || Number(row.dataset.qty || 1) === 1;
    this.classList.toggle('sn-kb--einzeln', single);
    if (single || !reset) return;
    this.scope.querySelectorAll('quantity-selector-component').forEach((q) => {
      const input = q.querySelector('input');
      if (!input || input.value === '1') return;
      if (typeof q.setValue === 'function') q.setValue('1');
      else input.value = '1';
      input.dispatchEvent(new Event('change', { bubbles: true }));
      // Kaufleiste unten zeigt sonst noch die alte Menge „(2)“ (hoert nur auf Horizons quantity-selector:update)
      q.dispatchEvent(new CustomEvent('quantity-selector:update', { bubbles: true, detail: { quantity: 1 } }));
    });
  }
}

if (!customElements.get('sn-kaufboxen')) customElements.define('sn-kaufboxen', SnKaufboxen);
